import { useEffect, useState } from 'react';
import { LOGO_BASE_URL } from '../../apiConfig';
import { Trophy, Smartphone, Mail, Globe, ArrowRight, Shield } from 'lucide-react';

function TeamProfileHeader({ data }) {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth <= 1024;
    const accentYellow = '#E7C424';

    const styles = {
        wrapper: {
            width: '100%',
            background: 'radial-gradient(circle at center, #0B15C5 0%, #060D8A 100%)',
            padding: isMobile ? '32px 16px' : '40px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            position: 'relative',
            color: '#FFFFFF',
            overflow: 'hidden',
        },
        decorationGlow: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            height: '600px',
            background: 'rgba(231,196,36,0.05)',
            filter: 'blur(100px)',
            borderRadius: '50%',
            zIndex: 0,
        },
        brandBlock: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            zIndex: 1,
            textAlign: 'center',
            width: '100%',
        },
        logoBox: {
            width: isMobile ? '120px' : '140px',
            height: isMobile ? '120px' : '140px',
            background: '#FFFFFF',
            borderRadius: '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '12px',
            boxShadow: '0 15px 30px rgba(0,0,0,0.4)',
            border: '1px solid rgba(255,255,255,0.1)',
        },
        teamName: {
            fontFamily: 'var(--fontAnton)',
            fontSize: isMobile ? '36px' : '56px',
            fontWeight: 400,
            lineHeight: '100%',
            textTransform: 'uppercase',
            margin: 0,
            letterSpacing: '-1px',
            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
        },
        identityBar: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: isMobile ? '16px' : '24px',
            background: 'rgba(0,0,0,0.2)',
            padding: isMobile ? '8px 20px' : '8px 24px',
            borderRadius: '40px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            marginTop: '0px',
        },
        statItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'var(--fontInter)',
            fontSize: isMobile ? '11px' : '13px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        divider: {
            width: '1px',
            height: '16px',
            background: 'rgba(255,255,255,0.2)',
            display: 'block',
        },
        footerActions: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? '16px' : '40px',
            marginTop: '16px',
            zIndex: 1,
            width: '100%',
            justifyContent: 'center',
        },
        websiteBtn: {
            background: '#FFFFFF',
            color: '#060D8A',
            padding: '10px 24px',
            textDecoration: 'none',
            fontFamily: 'var(--fontAnton)',
            fontWeight: 400,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textTransform: 'uppercase',
            transition: 'all 0.3s ease',
            letterSpacing: '1px',
        },
        contactList: {
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
        contactItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'var(--fontInter)',
            fontWeight: 600,
            fontSize: '13px',
            opacity: 0.9,
            transition: 'opacity 0.2s ease',
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.decorationGlow} />

            <div style={styles.brandBlock}>
                <div style={styles.logoBox}>
                    <img 
                        src={data.imglogo ? `${LOGO_BASE_URL}/${data.imglogo}` : ""} 
                        alt={data.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                    />
                </div>

                <h1 style={styles.teamName}>{data.name}</h1>

                <div style={styles.identityBar}>
                    <div style={styles.statItem}>
                        <Shield size={16} fill={accentYellow} color={accentYellow} />
                        <span>{data.division_name || 'PREMIER'}</span>
                    </div>
                    
                    <div style={styles.divider} />

                    <div style={styles.statItem}>
                        <Trophy size={16} color={accentYellow} />
                        <span style={{color: accentYellow}}>{data.champions_years || '2015, 2018, 2020'}</span>
                    </div>

                    <div style={styles.divider} />

                    <div style={styles.statItem}>
                        <Globe size={16} style={{opacity: 0.6}} />
                        <span style={{opacity: 0.6}}>ID: {data.pc_team_id}</span>
                    </div>
                </div>
            </div>

            <div style={styles.footerActions}>
                <a href={data.web_link} target="_blank" rel="noreferrer" style={styles.websiteBtn}>
                    Visit Website <ArrowRight size={18} />
                </a>

                <div style={styles.contactList}>
                    <div style={styles.contactItem} title="Phone">
                        <Smartphone size={18} color={accentYellow} />
                        <span>{data.contactno}</span>
                    </div>
                    <div style={styles.contactItem} title="Email">
                        <Mail size={18} color={accentYellow} />
                        <span style={{textTransform: 'lowercase'}}>{data.email}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TeamProfileHeader;